import { calculateExpenseAtMonth, calculateRevenueAtMonth } from "./scaling.utils";
import {
  calculateMonthlyCustomerMetrics,
  type CustomerProjectionData,
  type MonthlyCustomerMetrics,
} from "./customer-projection.utils";
import {
  calculateAssetValue,
  calculateAssetMonthlyImpact,
  calculateAssetPurchaseCashFlow,
  resolveActiveAssetEvents,
  resolveActiveRevenues,
  resolveActiveExpenses,
  startOfMonthUTC,
  isOnOrAfterStartupStart,
  isStartupStartMonth,
  sumFundingEventsInMonth,
  sumLiquidAssetBookValue,
} from "@burnchart/financial";
import { CHART_COLORS } from "@/constants/chart-colors";
import {
  PROJECTED_CUSTOMER_ACQUISITION_EXPENSE_ID,
  PROJECTED_CUSTOMER_SERVICING_EXPENSE_ID,
  usesProjectedCustomerAcquisitionCosts,
  shouldExcludeAcquisitionExpenseFromBurn,
} from "./acquisition-expense.utils";

export {
  calculateAssetValue,
  calculateAssetMonthlyImpact,
  calculateAssetPurchaseCashFlow,
  resolveActiveAssetEvents,
};

function endOfMonthUTC(monthStart: Date): Date {
  const end = new Date(monthStart);
  end.setUTCMonth(end.getUTCMonth() + 1);
  end.setUTCDate(0);
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

interface ExpenseData {
  id: string;
  name: string;
  category: string;
  monthlyAmount?: number; // Made optional for one-time expenses
  oneTimeAmount?: number; // For one-time expenses
  startDate: Date;
  endDate?: Date;
  color: string;
  // New scaling fields
  expenseType?: "fixed" | "percentage" | "hybrid" | "customer_based";
  growthRate?: number;
  revenueRatio?: number;
  // Customer-based expense fields
  customerBaseCostPerCustomer?: number;
  customerBaseAmount?: number;
  isOneTime?: boolean; // Flag for one-time expenses
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
}

export interface RevenueData {
  id: string;
  name: string;
  category: "funding" | "sales" | "investment" | "grant" | "other";
  monthlyAmount: number; // For recurring revenue, or monthly equivalent
  startDate: Date;
  endDate?: Date;
  isRecurring: boolean;
  frequency: "monthly" | "quarterly" | "yearly" | "one-time";
  color: string;
  // New scaling fields
  growthRate?: number;
  // Customer-driven revenue fields
  isCustomerDriven?: boolean;
  customerGrowthModelId?: string;
  subscriptionTierIds?: string[];
  customerCountMultiplier?: number;
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
}

interface EventData {
  id: string;
  date: Date;
  type: "funding" | "milestone" | "launch" | "asset" | "custom";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  // Asset-specific fields
  monthlyAppreciationRate?: number; // Monthly appreciation/depreciation rate (percentage)
  minimumValue?: number; // Minimum value for depreciating assets (optional, defaults to 0)
  endDate?: Date;
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
  suppressPurchaseCashFlow?: boolean;
}

interface ChartData {
  startDate: Date;
  endDate: Date;
  initialBalance: number;
  expenses: ExpenseData[];
  revenues?: RevenueData[];
  events: EventData[];
  customerProjectionData?: CustomerProjectionData;
}

export interface BurndownSnapshot {
  date: Date;
  cashBalance: number;
  monthlyBurn: number;
  monthlyRevenue: number;
  netMonthlyChange: number;
  activeExpenses: ExpenseData[];
  activeRevenues: RevenueData[];
  runway: number;
}

function buildExpenseForCalculation(expense: ExpenseData) {
  return {
    ...expense,
    amount:
      expense.expenseType === "customer_based" ? 0 : expense.monthlyAmount || 0,
    frequency: "monthly" as const,
    expenseType: expense.expenseType || "fixed",
    growthRate: expense.growthRate,
    revenueRatio: expense.revenueRatio,
    customerBaseCostPerCustomer: expense.customerBaseCostPerCustomer,
    customerBaseAmount: expense.customerBaseAmount,
    isRecurring: true,
    startupId: "",
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Calculates the financial snapshot for a given target date
 * @param data The burndown chart data
 * @param targetDate The date to calculate the snapshot for
 * @returns Financial snapshot including cash balance, active expenses, and runway
 */
export function calculateBurndownSnapshot(
  data: ChartData,
  targetDate: Date,
): BurndownSnapshot {
  let cashBalance = 0;
  let monthlyBurn = 0;
  let monthlyRevenue = 0;
  const activeExpenses: ExpenseData[] = [];
  const activeRevenues: RevenueData[] = [];

  const startOfMonth = startOfMonthUTC(data.startDate);
  const targetMonth = startOfMonthUTC(targetDate);

  // Calculate cash flow through each month up to target
  while (startOfMonth.getTime() <= targetMonth.getTime()) {
    if (isStartupStartMonth(data.startDate, startOfMonth)) {
      cashBalance += data.initialBalance || 0;
    }

    if (!isOnOrAfterStartupStart(data.startDate, startOfMonth)) {
      startOfMonth.setUTCMonth(startOfMonth.getUTCMonth() + 1);
      continue;
    }
    // Calculate monthly revenues first (needed for percentage-based expenses)
    const monthlyRevenues = resolveActiveRevenues(
      data.revenues || [],
      startOfMonth,
    ).filter((revenue) => {
      if (revenue.frequency === "one-time") return false;
      if (data.customerProjectionData && revenue.isCustomerDriven) return false;
      return true;
    });

    // Calculate month revenue using scaling utilities
    const monthRevenue = monthlyRevenues.reduce((sum, revenue) => {
      const revenueForCalculation = {
        ...revenue,
        type: revenue.category as
          | "funding"
          | "sales"
          | "investment"
          | "grant"
          | "other",
        amount: revenue.monthlyAmount,
        date: revenue.startDate,
        startupId: "",
        createdAt: new Date(),
        updatedAt: new Date(),
        frequency:
          revenue.frequency === "one-time" ? "monthly" : revenue.frequency,
      };
      return sum + calculateRevenueAtMonth(revenueForCalculation, startOfMonth);
    }, 0);

    // Add customer projection revenue if available
    let loopCustomerMetrics: MonthlyCustomerMetrics | undefined;
    if (data.customerProjectionData) {
      loopCustomerMetrics = calculateMonthlyCustomerMetrics(
        startOfMonth,
        data.customerProjectionData,
      );
    }
    const customerRevenue = loopCustomerMetrics?.subscriptionRevenue ?? 0;

    const recurringMonthRevenue = monthRevenue + customerRevenue;

    // Calculate monthly recurring expenses using scaling utilities
    const monthlyExpenses = resolveActiveExpenses(data.expenses, startOfMonth).filter(
      (expense) =>
        !shouldExcludeAcquisitionExpenseFromBurn(
          expense,
          data.customerProjectionData,
        ),
    );

    const monthBurn =
      monthlyExpenses.reduce((sum, expense) => {
        return (
          sum +
          calculateExpenseAtMonth(
            buildExpenseForCalculation(expense),
            startOfMonth,
            recurringMonthRevenue,
            loopCustomerMetrics,
          )
        );
      }, 0) +
      (loopCustomerMetrics?.acquisitionCosts ?? 0) +
      (loopCustomerMetrics?.servicingCosts ?? 0);

    // Handle one-time expenses for this specific month
    const monthEnd =
      startOfMonth.getTime() === targetMonth.getTime()
        ? targetDate
        : endOfMonthUTC(startOfMonth);

    const oneTimeExpensesThisMonth = data.expenses.filter((expense) => {
      if (!expense.isOneTime) return false;
      const amount = expense.oneTimeAmount || expense.monthlyAmount || 0;
      if (!amount || !expense.startDate) return false;

      if (
        startOfMonthUTC(expense.startDate).getTime() !== startOfMonth.getTime()
      ) {
        return false;
      }

      return monthEnd.getTime() >= expense.startDate.getTime();
    });

    const oneTimeExpenseTotal = oneTimeExpensesThisMonth.reduce(
      (sum, expense) => {
        return sum + (expense.oneTimeAmount || expense.monthlyAmount || 0);
      },
      0,
    );

    const oneTimeRevenuesThisMonth = (data.revenues || []).filter((revenue) => {
      if (revenue.frequency !== "one-time" || !revenue.startDate) return false;
      return (
        startOfMonthUTC(revenue.startDate).getTime() === startOfMonth.getTime()
      );
    });

    const oneTimeRevenueTotal = oneTimeRevenuesThisMonth.reduce(
      (sum, revenue) => sum + (revenue.monthlyAmount || 0),
      0,
    );

    const assetPurchaseCash = resolveActiveAssetEvents(data.events, startOfMonth).reduce(
      (sum, asset) => sum + calculateAssetPurchaseCashFlow(asset, startOfMonth),
      0,
    );

    const fundingThisMonth = sumFundingEventsInMonth(data.events, startOfMonth);

    cashBalance +=
      recurringMonthRevenue +
      oneTimeRevenueTotal -
      monthBurn -
      oneTimeExpenseTotal +
      assetPurchaseCash +
      fundingThisMonth;

    startOfMonth.setUTCMonth(startOfMonth.getUTCMonth() + 1);
  }

  // Calculate active recurring expenses for the target month specifically
  const targetMonthRecurringExpenses = resolveActiveExpenses(
    data.expenses,
    targetMonth,
  ).filter(
    (expense) =>
      !shouldExcludeAcquisitionExpenseFromBurn(
        expense,
        data.customerProjectionData,
      ),
  );

  // Calculate active revenues for the target month specifically
  const targetMonthRevenues = resolveActiveRevenues(
    data.revenues || [],
    targetMonth,
  ).filter((revenue) => revenue.frequency !== "one-time");

  // Include both recurring and one-time expenses in activeExpenses for monthly breakdown
  activeExpenses.push(...targetMonthRecurringExpenses);
  
  // Add one-time expenses for the target month
  const oneTimeExpensesTargetMonth = data.expenses.filter((expense) => {
    if (!expense.isOneTime) return false;
    const amount = expense.oneTimeAmount || expense.monthlyAmount || 0;
    if (!amount || !expense.startDate) return false;

    return (
      startOfMonthUTC(expense.startDate).getTime() === targetMonth.getTime() &&
      targetDate.getTime() >= expense.startDate.getTime()
    );
  });
  
  activeExpenses.push(...oneTimeExpensesTargetMonth);
  activeRevenues.push(...targetMonthRevenues);
  
  // Add one-time revenues for the target month
  const oneTimeRevenuesTargetMonth = (data.revenues || []).filter((revenue) => {
    if (revenue.frequency !== "one-time") return false;

    return (
      startOfMonthUTC(revenue.startDate).getTime() === targetMonth.getTime()
    );
  });
  
  activeRevenues.push(...oneTimeRevenuesTargetMonth);

  // Calculate monthly revenue first for expense calculations
  let recurringMonthlyRevenue = targetMonthRevenues.reduce((sum, revenue) => {
    const revenueForCalculation = {
      ...revenue,
      type: revenue.category as
        | "funding"
        | "sales"
        | "investment"
        | "grant"
        | "other",
      amount: revenue.monthlyAmount,
      frequency:
        revenue.frequency === "one-time" ? "monthly" : revenue.frequency,
      date: revenue.startDate,
      startupId: "", // Not needed for calculation
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return sum + calculateRevenueAtMonth(revenueForCalculation, targetMonth);
  }, 0);

  // Add customer projection revenue for the target month
  if (data.customerProjectionData) {
    const customerMetrics = calculateMonthlyCustomerMetrics(targetMonth, data.customerProjectionData);
    recurringMonthlyRevenue += customerMetrics.subscriptionRevenue;
  }

  // Add one-time revenues for the target month
  const oneTimeRevenueTargetMonthTotal = oneTimeRevenuesTargetMonth.reduce(
    (sum, revenue) => sum + (revenue.monthlyAmount || 0),
    0,
  );
  monthlyRevenue = recurringMonthlyRevenue + oneTimeRevenueTargetMonthTotal;

  // Calculate monthly burn using the calculated revenue (recurring expenses)
  let targetCustomerMetrics: MonthlyCustomerMetrics | undefined;
  if (data.customerProjectionData) {
    targetCustomerMetrics = calculateMonthlyCustomerMetrics(
      targetMonth,
      data.customerProjectionData,
    );
  }

  monthlyBurn = targetMonthRecurringExpenses.reduce((sum, expense) => {
    return (
      sum +
      calculateExpenseAtMonth(
        buildExpenseForCalculation(expense),
        targetMonth,
        recurringMonthlyRevenue,
        targetCustomerMetrics,
      )
    );
  }, 0);
  
  // Add one-time expenses for the target month to monthlyBurn
  const oneTimeExpenseTargetMonthTotal = oneTimeExpensesTargetMonth.reduce(
    (sum, expense) => sum + (expense.oneTimeAmount || expense.monthlyAmount || 0),
    0,
  );
  monthlyBurn += oneTimeExpenseTargetMonthTotal;

  // Add asset depreciation to monthly burn
  const assetDepreciationExpenses: ExpenseData[] = [];
  const assetDepreciation = resolveActiveAssetEvents(data.events, targetMonth).reduce(
    (sum, asset) => {
      const impact = calculateAssetMonthlyImpact(asset, targetMonth);
      if (impact < 0) {
        // Create virtual expense entry for asset depreciation
        assetDepreciationExpenses.push({
          id: `asset-depreciation-${asset.id}`,
          name: `${asset.description} Depreciation`,
          category: "Asset Depreciation",
          monthlyAmount: Math.abs(impact),
          startDate: targetMonth,
          endDate: undefined,
          color: "#f59e0b",
          expenseType: "fixed",
          isOneTime: false,
        });
        return sum + Math.abs(impact); // Only negative impact counts as expense
      }
      return sum;
    }, 0);
  
  // Add asset depreciation expenses to activeExpenses
  activeExpenses.push(...assetDepreciationExpenses);
  monthlyBurn += assetDepreciation;

  if (data.customerProjectionData && targetCustomerMetrics) {
    const customerMetrics = targetCustomerMetrics;
    monthlyBurn += customerMetrics.acquisitionCosts;
    monthlyBurn += customerMetrics.servicingCosts ?? 0;

    if (
      usesProjectedCustomerAcquisitionCosts(data.customerProjectionData) &&
      customerMetrics.acquisitionCosts > 0
    ) {
      activeExpenses.push({
        id: PROJECTED_CUSTOMER_ACQUISITION_EXPENSE_ID,
        name: "Customer Acquisition",
        category: "Customer Acquisition",
        monthlyAmount: customerMetrics.acquisitionCosts,
        startDate: targetMonth,
        endDate: undefined,
        color: CHART_COLORS.expense,
        expenseType: "fixed",
        isOneTime: false,
      });
    }

    if ((customerMetrics.servicingCosts ?? 0) > 0) {
      activeExpenses.push({
        id: PROJECTED_CUSTOMER_SERVICING_EXPENSE_ID,
        name: "Customer Servicing",
        category: "Customer Servicing",
        monthlyAmount: customerMetrics.servicingCosts ?? 0,
        startDate: targetMonth,
        endDate: undefined,
        color: CHART_COLORS.expenseMuted,
        expenseType: "fixed",
        isOneTime: false,
      });
    }
  }

  // Liquid assets add to available cash (purchase outflow already applied in the loop)
  const liquidAssetValue = sumLiquidAssetBookValue(data.events, targetDate);
  cashBalance += liquidAssetValue;

  const netMonthlyChange = monthlyRevenue - monthlyBurn;
  const runway =
    netMonthlyChange >= 0
      ? Infinity
      : Math.max(0, cashBalance / Math.abs(netMonthlyChange));

  return {
    date: targetDate,
    cashBalance,
    monthlyBurn,
    monthlyRevenue,
    netMonthlyChange,
    activeExpenses,
    activeRevenues,
    runway,
  };
}

/**
 * Find dates when cash flow transitions from positive to zero/negative
 */
export function findCashTransitions(data: ChartData): Array<{
  date: Date;
  balance: number;
  type: 'zero' | 'negative';
}> {
  const transitions: Array<{
    date: Date;
    balance: number;
    type: 'zero' | 'negative';
  }> = [];

  // Sample cash balance daily from start to end date
  const currentDate = new Date(data.startDate);
  let previousBalance = data.initialBalance;
  let wasPositive = previousBalance > 0;

  while (currentDate <= data.endDate) {
    const snapshot = calculateBurndownSnapshot(data, currentDate);
    const currentBalance = snapshot.cashBalance;

    // Check for transition from positive to zero or negative
    if (wasPositive && currentBalance <= 0) {
      // Find the exact transition type
      const transitionType = currentBalance === 0 ? 'zero' : 'negative';
      
      transitions.push({
        date: new Date(currentDate),
        balance: currentBalance,
        type: transitionType,
      });
      
      wasPositive = false;
    } else if (!wasPositive && currentBalance > 0) {
      // Cash went positive again (e.g., after funding)
      wasPositive = true;
    }

    previousBalance = currentBalance;
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return transitions;
}

/**
 * Groups active expenses by category for display
 * @param expenses Array of active expenses
 * @returns Object with expenses grouped by category
 */
export function groupExpensesByCategory(expenses: ExpenseData[]) {
  return expenses.reduce((acc, expense) => {
    if (!acc[expense.category]) {
      acc[expense.category] = [];
    }
    acc[expense.category].push(expense);
    return acc;
  }, {} as Record<string, ExpenseData[]>);
}

/**
 * Groups active revenues by category for display
 * @param revenues Array of active revenues
 * @returns Object with revenues grouped by category
 */
export function groupRevenuesByCategory(revenues: RevenueData[]) {
  return revenues.reduce((acc, revenue) => {
    if (!acc[revenue.category]) {
      acc[revenue.category] = [];
    }
    acc[revenue.category].push(revenue);
    return acc;
  }, {} as Record<string, RevenueData[]>);
}

/**
 * Normalizes revenue amount to monthly based on frequency
 * @param amount The revenue amount
 * @param frequency The revenue frequency
 * @returns Monthly equivalent amount
 */
export function normalizeRevenueToMonthly(
  amount: number,
  frequency: "monthly" | "quarterly" | "yearly" | "one-time",
): number {
  const multipliers = {
    monthly: 1,
    quarterly: 1 / 3,
    yearly: 1 / 12,
    "one-time": 0, // One-time revenues don't contribute to recurring monthly
  };

  return amount * (multipliers[frequency] || 0);
}

/**
 * Normalizes expense amount to monthly based on frequency
 * @param amount The expense amount
 * @param frequency The expense frequency
 * @returns Monthly equivalent amount
 */
export function normalizeExpenseToMonthly(
  amount: number,
  frequency: "monthly" | "quarterly" | "yearly" | "one-time",
): number {
  const multipliers = {
    monthly: 1,
    quarterly: 1 / 3,
    yearly: 1 / 12,
    "one-time": 0, // One-time expenses don't contribute to recurring monthly burn
  };

  return amount * (multipliers[frequency] || 0);
}
